import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks
export const fetchWishlist = createAsyncThunk(
  'wishlist/fetchWishlist',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getWishlist();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addToWishlist = createAsyncThunk(
  'wishlist/addToWishlist',
  async (menuId, { rejectWithValue }) => {
    try {
      const response = await api.addToWishlist(menuId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const removeFromWishlist = createAsyncThunk(
  'wishlist/removeFromWishlist',
  async (menuId, { rejectWithValue }) => {
    try {
      await api.removeFromWishlist(menuId);
      return menuId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateWishlist = createAsyncThunk(
  'wishlist/updateWishlist',
  async (menuIds, { rejectWithValue }) => {
    try {
      const response = await api.updateWishlist(menuIds);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchVendorWishlist = createAsyncThunk(
  'wishlist/fetchVendorWishlist',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getVendorWishlist();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addToVendorWishlist = createAsyncThunk(
  'wishlist/addToVendorWishlist',
  async (vendorId, { rejectWithValue }) => {
    try {
      const response = await api.addToVendorWishlist(vendorId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const removeFromVendorWishlist = createAsyncThunk(
  'wishlist/removeFromVendorWishlist',
  async (vendorId, { rejectWithValue }) => {
    try {
      await api.removeFromVendorWishlist(vendorId);
      return vendorId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateVendorWishlist = createAsyncThunk(
  'wishlist/updateVendorWishlist',
  async (vendorIds, { rejectWithValue }) => {
    try {
      const response = await api.updateVendorWishlist(vendorIds);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  menuItems: [],
  vendorItems: [],
  loading: false,
  error: null,
  lastUpdated: null,
};

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    addMenuItemOptimistic: (state, action) => {
      const menuItem = action.payload;
      const exists = state.menuItems.some(item => item.id === menuItem.id);
      if (!exists) {
        state.menuItems.push(menuItem);
      }
    },
    removeMenuItemOptimistic: (state, action) => {
      const menuId = action.payload;
      state.menuItems = state.menuItems.filter(item => item.id !== menuId);
    },
    addVendorItemOptimistic: (state, action) => {
      const vendorItem = action.payload;
      const exists = state.vendorItems.some(item => item.id === vendorItem.id);
      if (!exists) {
        state.vendorItems.push(vendorItem);
      }
    },
    removeVendorItemOptimistic: (state, action) => {
      const vendorId = action.payload;
      state.vendorItems = state.vendorItems.filter(item => item.id !== vendorId);
    },
    clearWishlist: (state) => {
      state.menuItems = [];
      state.vendorItems = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Wishlist
      .addCase(fetchWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.menuItems = action.payload.menu_items || [];
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add to Wishlist
      .addCase(addToWishlist.fulfilled, (state, action) => {
        const menuItem = action.payload;
        const exists = state.menuItems.some(item => item.id === menuItem.id);
        if (!exists) {
          state.menuItems.push(menuItem);
        }
        state.lastUpdated = new Date().toISOString();
      })
      // Remove from Wishlist
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        const menuId = action.payload;
        state.menuItems = state.menuItems.filter(item => item.id !== menuId);
        state.lastUpdated = new Date().toISOString();
      })
      // Update Wishlist
      .addCase(updateWishlist.fulfilled, (state, action) => {
        state.menuItems = action.payload.menu_items || [];
        state.lastUpdated = new Date().toISOString();
      })
      // Fetch Vendor Wishlist
      .addCase(fetchVendorWishlist.fulfilled, (state, action) => {
        state.vendorItems = action.payload.vendors || [];
        state.lastUpdated = new Date().toISOString();
      })
      // Add to Vendor Wishlist
      .addCase(addToVendorWishlist.fulfilled, (state, action) => {
        const vendorItem = action.payload;
        const exists = state.vendorItems.some(item => item.id === vendorItem.id);
        if (!exists) {
          state.vendorItems.push(vendorItem);
        }
        state.lastUpdated = new Date().toISOString();
      })
      // Remove from Vendor Wishlist
      .addCase(removeFromVendorWishlist.fulfilled, (state, action) => {
        const vendorId = action.payload;
        state.vendorItems = state.vendorItems.filter(item => item.id !== vendorId);
        state.lastUpdated = new Date().toISOString();
      })
      // Update Vendor Wishlist
      .addCase(updateVendorWishlist.fulfilled, (state, action) => {
        state.vendorItems = action.payload.vendors || [];
        state.lastUpdated = new Date().toISOString();
      });
  },
});

export const {
  clearError,
  addMenuItemOptimistic,
  removeMenuItemOptimistic,
  addVendorItemOptimistic,
  removeVendorItemOptimistic,
  clearWishlist,
} = wishlistSlice.actions;

export default wishlistSlice.reducer;
